/**
 * Validators Registry
 *
 * Named validation functions that can be referenced in the form schema.
 * Each validator returns an error message string or null if valid.
 */

import type { ValidatorFn } from '../../config/intakeFormSchema';
import { formatMbi } from '../../utils/intake';

export type ValidatorFunction = (
  value: string | null | undefined
) => string | null;

// =============================================================================
// Validation Patterns
// =============================================================================

const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/;
const PHONE_PATTERN = /^\d{3}-\d{3}-\d{4}$/;
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// =============================================================================
// Individual Validators
// =============================================================================

const validateFirstName: ValidatorFunction = value => {
  if (!value) return null;
  if (value.length > 19) return 'First name must be 19 characters or less';
  return null;
};

const validateLastName: ValidatorFunction = value => {
  if (!value) return null;
  if (value.length > 20) return 'Last name must be 20 characters or less';
  return null;
};

const validateSsn: ValidatorFunction = value => {
  if (!value) return null;
  if (!SSN_PATTERN.test(value)) return 'SSN must be in format XXX-XX-XXXX';
  return null;
};

const validatePhone: ValidatorFunction = value => {
  if (!value) return null;
  if (!PHONE_PATTERN.test(value)) return 'Phone must be in format aaa-bbb-cccc';
  return null;
};

const validateEmail: ValidatorFunction = value => {
  if (!value) return null;
  if (!EMAIL_PATTERN.test(value)) return 'Invalid email format';
  return null;
};

const validateZip: ValidatorFunction = value => {
  if (!value) return null;
  if (!ZIP_PATTERN.test(value)) return 'Zip code must be 12345 or 12345-6789';
  return null;
};

const validateMbi: ValidatorFunction = value => {
  if (!value?.trim()) return null;
  if (value.includes('-')) {
    return 'MBI must not contain dashes';
  }
  const sanitized = formatMbi(value);
  if (sanitized.length > 11) {
    return 'MBI must be 11 characters or less';
  }
  return null;
};

// =============================================================================
// Validator Registry
// =============================================================================

const validatorRegistry: Record<ValidatorFn, ValidatorFunction> = {
  validate_first_name: validateFirstName,
  validate_last_name: validateLastName,
  validate_ssn: validateSsn,
  validate_phone: validatePhone,
  validate_email: validateEmail,
  validate_zip: validateZip,
  validate_mbi: validateMbi,
};

// =============================================================================
// Public API
// =============================================================================

/**
 * Get a validator function by name
 */
export const getValidator = (name: ValidatorFn): ValidatorFunction => {
  return validatorRegistry[name];
};

/**
 * Run validation for a field using the named validator
 * Returns error message or null if valid
 */
export const runValidation = (
  validatorName: ValidatorFn | undefined,
  value: string | null | undefined
): string | null => {
  if (!validatorName) return null;
  const validator = getValidator(validatorName);
  if (!validator) {
    console.warn(`Validator not found: ${validatorName}`);
    return null;
  }
  return validator(value);
};
